'use strict'

import {useIntl} from 'react-intl'
import {useContext} from 'react'
import {IPContext} from '../custom-contexts'
import AlertMgr from '../custom-api/alert-custom-api'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from './use-multi-site'

/**
 * Provides functionalities to interact with user profiles, including searching for
 * an email in the newsletter database and updating custom attributes for a user profile.
 *
 * This hook utilizes the current site and locale context and requires an access token
 * for making authenticated API requests. It offers two main asynchronous functions:
 * `searchEmail` for checking the existence of an email in the newsletter profiles,
 * and `updateCustomAttributes` for modifying user profile attributes.
 *
 * @returns {Object} An object containing the following methods:
 *
 * @property {Function} searchEmail - Asynchronously searches for a profile email in the newsletter database.
 * @property {Function} updateCustomAttributes - Updates custom attributes for a user profile.
 */
export const useXSSProtection = () => {
    const {getTokenWhenReady} = useAccessToken()
    const {app: appConfig} = getConfig()
    const {site, locale} = useMultiSite()
    const {ip: userIP, setIsIPBlocked} = useContext(IPContext)
    const {formatMessage} = useIntl()

    const encodeHTML = (str) => {
        return str
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;')
    }

    const xssPatterns = [/<script/gi, /<a/gi, /(https?|s?ftp):\/\/[^\s]+/gi, /:\s*\/\/\s*[^\s]*/gi]
    /**
     * Validates a form field's content for potential XSS (Cross-Site Scripting) attacks and sends an alert if detected.
     *
     * @async
     * @param {string} fieldValue - The value of the form field to be validated.
     * @param {string} formId - The identifier of the form containing the field.
     * @param {string} fieldId - The identifier of the field being validated.
     * @param {boolean} isSearchPhrase - A flag indicating whether the field value is a search phrase. If true, no alert will be sent.
     * @returns {Promise<boolean>} - A promise that resolves to `true` if XSS content is detected and an alert is sent, otherwise the function does not return.
     *
     * @description
     * This function checks the provided form field value against a set of XSS patterns. If a match is found, it constructs an alert with relevant context information and sends it using the `AlertMgr` service. The alert includes details such as the site ID, organization ID, locale, form ID, field ID, sanitized field value, and the user's IP address. The alert is categorized under 'Form XSS Validation' and is marked as a 'technical' alert with no logging.
     */
    const validateXssContent = async (fieldValue, formId, fieldId, isSearchPhrase) => {
        const accessToken = await getTokenWhenReady()
        const isXssDetected = xssPatterns.some((pattern) => pattern.test(fieldValue))

        if (isXssDetected) {
            // no need to send email alert if the phrase is being validated before user submits the search
            if (isSearchPhrase) {
                return true
            }

            const alertMgr = new AlertMgr({
                parameters: {
                    siteId: site.id,
                    organizationId: appConfig.commerceAPI.parameters.organizationId,
                    locale: locale.id
                }
            })

            alertMgr
                .sendAlert(
                    {
                        context: {
                            siteId: site.id,
                            message: 'A form field was found with XSS content',
                            category: 'XSS Protection',
                            order: '',
                            page: '',
                            job: '',
                            asset: '',
                            identifier: userIP,
                            fileName: '',
                            lineNumber: '',
                            formId: formId,
                            fieldId: fieldId,
                            fieldValue: encodeHTML(fieldValue),
                            ipRemoteAddress: userIP
                        },
                        alertType: 'technical',
                        logging: 'no'
                    },
                    accessToken
                )
                .then((response) => {
                    if (response.isIPBlocked) {
                        // Update isIPBlocked value in context
                        setIsIPBlocked(true)
                    }
                })

            return true
        }
        return false
    }

    /**
     * Validates form data to detect potential XSS (Cross-Site Scripting) attacks.
     * Iterates over each key-value pair in the provided data object, converts the value to a string,
     * and checks it against a predefined set of XSS patterns.
     *
     * @param {Object} data - The form data object where each key represents a form field and the value is the input to be validated.
     * @returns {boolean} - Returns true if any of the form fields match an XSS pattern, indicating a potential XSS attack; otherwise, returns false.
     */
    const validateXssForm = (data) => {
        for (const key in data) {
            if (key in data) {
                const fieldValue = data[key]

                // Convert the field value to a string
                const fieldValueStr = String(fieldValue)

                // Check if any pattern matches the field value
                const isXssDetected = xssPatterns.some((pattern) => pattern.test(fieldValueStr))

                // If an XSS pattern is detected, return true
                if (isXssDetected) {
                    return true
                }
            }
        }
        return false
    }

    /**
     * Validates a form field for potential XSS (Cross-Site Scripting) content and displays a toast notification if detected.
     *
     * @async
     * @param {Function} toast - A function to display toast notifications. It accepts an object with properties such as title, status.
     * @param {string} value - The value of the form field to be validated for XSS content.
     * @param {string} formName - The name of the form containing the field being validated.
     * @param {string} fieldName - The name of the field being validated.
     * @returns {Promise<void>} A promise that resolves when the validation and potential toast notification are complete.
     */
    const validateField = async (toast, value, formName, fieldName) => {
        const isXssDetected = await validateXssContent(value, formName, fieldName)

        if (isXssDetected) {
            toast({
                title: formatMessage({
                    defaultMessage: 'Web links of any sort are refused',
                    id: 'use_form_fields.error.xss_detected'
                }),
                status: 'error'
            })
        }
    }

    return {
        validateXssContent,
        validateXssForm,
        validateField
    }
}
